import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowUp, MessageCircle, ShieldCheck, Truck } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { SOCIAL_NETWORK_LABELS } from '@meshsmith/shared';
import { Logo } from '../Logo';
import { SocialIcon } from '../SocialIcon';
import { CallUs } from '../CallUs';
import { useSocialLinks } from '../../hooks/useSocialLinks';

const columns = [
  {
    title: 'Shop',
    links: [
      { label: '3D Printing', to: '/c/3d-printing' },
      { label: 'DIY Kids Kits', to: '/c/diy-kids-kits' },
      { label: 'Moulds', to: '/c/moulds' },
      { label: 'Candles', to: '/c/candles' },
      { label: 'New Arrivals', to: '/new-arrivals' },
    ],
  },
  {
    title: 'Help',
    links: [
      { label: 'FAQ', to: '/faq' },
      { label: 'Shipping Policy', to: '/pages/shipping-policy' },
      { label: 'Returns & Refunds', to: '/pages/returns-refunds' },
      { label: 'Contact', to: '/contact' },
      { label: 'Custom Orders', to: '/custom-orders' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', to: '/pages/about' },
      { label: 'Reviews', to: '/reviews' },
      { label: 'Privacy Policy', to: '/pages/privacy-policy' },
      { label: 'Terms of Service', to: '/pages/terms' },
    ],
  },
];

const PILLS = [
  { icon: MessageCircle, text: 'Order updates on WhatsApp' },
  { icon: ShieldCheck, text: 'Secure online payments' },
  { icon: Truck, text: 'Ships across India' },
];

export function Footer() {
  const { pathname } = useLocation();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const social = useSocialLinks();
  // the home page already has its own newsletter card just above the footer
  const showSignup = pathname !== '/';

  const subscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/content/newsletter', { email, source: 'footer' });
      toast.push('Thanks for subscribing', 'success');
      setEmail('');
    } catch {
      toast.push('Could not subscribe right now', 'error');
    } finally {
      setBusy(false);
    }
  };

  const toTop = () => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
  };

  return (
    <footer className="mt-16 border-t bg-white" style={{ borderColor: 'var(--border)' }}>
      {/* a thin brand-coloured line is the only flourish */}
      <div aria-hidden className="h-0.5" style={{ background: 'var(--brand-gradient)' }} />

      <div className="container-ms grid gap-10 py-12 md:grid-cols-[1.5fr_1fr_1fr_1fr]">
        <div>
          <Logo markClassName="h-8 w-8" wordmarkClassName="text-lg" showTagline />
          <p className="mt-3 max-w-xs text-sm text-secondary-grey">
            From Mesh to Matter. A creative-making store for 3D printing, kids kits, moulds, candles
            and craft supplies. Designed and made in our Noida studio.
          </p>

          <CallUs className="mt-4" />

          <ul className="mt-4 flex flex-wrap gap-2">
            {PILLS.map((p) => (
              <li
                key={p.text}
                className="inline-flex items-center gap-1.5 rounded-full bg-pale-aqua px-3 py-1 text-xs font-medium text-charcoal"
              >
                <p.icon size={13} className="text-deep-teal" />
                {p.text}
              </li>
            ))}
          </ul>

          {social.length > 0 && (
            <div className="mt-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-secondary-grey">
                Follow us
              </p>
              <ul className="flex flex-wrap gap-2">
                {social.map((l) => {
                  const name =
                    l.network === 'other' ? (l.label ?? 'Link') : SOCIAL_NETWORK_LABELS[l.network];
                  return (
                    <li key={`${l.network}-${l.url}`}>
                      <a
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${name} (opens in a new tab)`}
                        title={name}
                        className="grid h-9 w-9 place-items-center rounded-full border text-charcoal transition duration-200 hover:-translate-y-0.5 hover:border-deep-teal hover:bg-pale-aqua hover:text-deep-teal focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-deep-teal"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        <SocialIcon network={l.network} size={17} />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {showSignup && (
            <form onSubmit={subscribe} className="mt-5 flex max-w-sm gap-2">
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email for new drops"
                className="field"
                aria-label="Email for new drops"
              />
              <button className="btn btn-primary" disabled={busy}>
                Join
              </button>
            </form>
          )}
        </div>

        <div className="grid grid-cols-2 gap-x-6 gap-y-8 md:contents">
          {columns.map((col) => (
            <nav key={col.title} aria-label={col.title}>
              <h3 className="mb-3 text-sm font-semibold">{col.title}</h3>
              <ul className="space-y-2 text-sm text-secondary-grey">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      className="transition-colors hover:text-deep-teal hover:underline hover:underline-offset-4"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
      </div>

      <div className="border-t py-4" style={{ borderColor: 'var(--border)' }}>
        <div className="container-ms flex flex-col items-center justify-between gap-2 text-xs text-secondary-grey sm:flex-row">
          <span>© {new Date().getFullYear()} MeshSmith. All rights reserved.</span>
          <span>Prices in INR · GST included where applicable</span>
          <button
            type="button"
            onClick={toTop}
            className="inline-flex items-center gap-1 font-medium text-charcoal transition-colors hover:text-deep-teal focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-deep-teal"
          >
            Back to top <ArrowUp size={13} />
          </button>
        </div>
      </div>
    </footer>
  );
}
